import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled, { css, keyframes } from 'styled-components';
import { NavBarHeight, SubNavBarHeight } from '../../../const';

const spinnerAnimation = keyframes`
  0% {
    opacity: 0;
  }
  99% {
    opacity: 0;
  }
  
  100% {
    opacity: 1;
  }
`;

const LoaderContainer = styled.div`
  background-color: ${(props) => props.theme.colors.bgPrimary};
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  position: fixed;
  z-index: ${(props) => props.theme.zIndex.loader};

  ${({ fullPage }) =>
    !fullPage &&
    css`
      animation: ${spinnerAnimation} 0.5s;
    `}
`;

const LoaderWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
`;

const SpinnerContainer = styled.div`
  width: ${(props) => props.size || 60}px;
  height: ${(props) => props.size || 60}px;

  position: relative;
  margin: auto;

  ${({ fullPage }) =>
    !fullPage &&
    css`
      animation: ${spinnerAnimation} 0.5s;
    `}
`;

const Bounce = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.themeColor};
  opacity: 0.6;
  position: absolute;
  top: 0;
  left: 0;

  -webkit-animation: sk-bounce 1.05s infinite ease-in-out;
  animation: sk-bounce 1.5s infinite ease-in-out;

  @-webkit-keyframes sk-bounce {
    0%,
    100% {
      -webkit-transform: scale(0);
    }
    50% {
      -webkit-transform: scale(1);
    }
  }

  @keyframes sk-bounce {
    0%,
    100% {
      transform: scale(0);
      -webkit-transform: scale(0);
    }
    50% {
      transform: scale(1);
      -webkit-transform: scale(1);
    }
  }

  ${(props) =>
    props.num === 2 &&
    css`
      -webkit-animation-delay: -0.75s;
      animation-delay: -0.75s;
    `}
`;

export const Spinner = ({ size, fullPage }) => (
  <SpinnerContainer size={size} fullPage={fullPage}>
    <Bounce num={1} />
    <Bounce num={2} />
  </SpinnerContainer>
);

Spinner.propTypes = {
  size: PropTypes.number,
  fullPage: PropTypes.bool,
};

const LinearLoaderContainer = styled.div`
  position: fixed;
  top: calc(${NavBarHeight}px + ${SubNavBarHeight}px);
  width: 100%;
  height: 3px;
  background-color: ${(props) => props.theme.colors.bgSecondary};
  z-index: ${(props) => props.theme.zIndex.navBar};

  ${(props) =>
    props.isAuth &&
    css`
      background-color: ${props.theme.colors.loader};
    `}

  &.modal-loader {
    position: absolute;
    max-width: 100%;
    top: 0;
    left: 0;
  }
`;

const LinearBar = styled.div`
  content: '';
  display: inline;
  position: absolute;
  width: 0;
  height: 3px;
  left: 50%;
  z-index: ${(props) => props.theme.zIndex.navBar};
  text-align: center;

  &:nth-child(1) {
    background-color: ${(props) => props.theme.colors.loader};
    -webkit-animation: loading 1.5s infinite;
    animation: loading 1.5s infinite;
    animation-timing-function: ease-in-out;

    ${(props) =>
      props.isAuth &&
      css`
        background-color: ${props.theme.colors.white};
      `}
  }
  @-webkit-keyframes loading {
    from {
      left: 50%;
      width: 0;
      z-index: 100;
    }
    to {
      left: 0;
      width: 100%;
      z-index: 10;
    }
  }
  @keyframes loading {
    from {
      left: 50%;
      width: 0;
      z-index: 100;
    }
    66.6666% {
      left: 0;
      width: 100%;
      z-index: 10;
    }
    to {
      left: 0;
      width: 100%;
    }
  }
`;

export const LinearLoader = ({ isAuth, className }) => (
  <LinearLoaderContainer isAuth={isAuth} className={className}>
    <LinearBar isAuth={isAuth} />
  </LinearLoaderContainer>
);
LinearLoader.propTypes = {
  isAuth: PropTypes.bool,
  className: PropTypes.string,
};

const Loader = ({ isSpinning, fullPage }) =>
  isSpinning ? (
    <LoaderContainer fullPage={fullPage}>
      <LoaderWrapper>
        <Spinner fullPage={fullPage} />
      </LoaderWrapper>
    </LoaderContainer>
  ) : null;

Loader.propTypes = {
  isSpinning: PropTypes.bool.isRequired,
  fullPage: PropTypes.bool,
};

export default connect((state) => ({
  isSpinning: state.app.loader.isSpinning,
  fullPage: state.app.loader?.fullPage,
}))(Loader);
